import { __decorate } from "tslib";
// NPM
import { Component, Mixins } from "vue-property-decorator";
import Switches from "vue-switches";
// SYSTEM1
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header.vue";
import ClientDetailsMixin from "./ClientDetailsMixin";
import ClientModule from "@/store/modules/client.module";
import EcGrid from "@/components/widgets/EcGrid.vue";
import { clientRiskColorName } from "@/store/models/client/clientRisk";
/**
 * Client Health: total contacts, teams, active members, age with ECG and latest risk for every client
 */
let ClientHealth = /** @class */ (() => {
    let ClientHealth = class ClientHealth extends Mixins(ClientDetailsMixin) {
        constructor() {
            // DATA
            super(...arguments);
            this.title = "Client Health";
            this.items = [
                {
                    text: "Home",
                    to: "/dashBoard",
                },
                {
                    text: "Customer Service",
                    href: "#",
                },
                {
                    text: "Client Health",
                    active: true,
                },
            ];
            // Grid
            this.isGridLoading = false;
            this.rows = [];
            this.totalCount = 0;
            // Off: active clients only; on: deactivated clients only
            this.showInactive = false;
            this.initialSortBy = {
                field: "name",
                type: "asc",
            };
            this.columns = [
                {
                    field: "name",
                    label: "Client",
                    tdClass: "health-client",
                    sortable: true,
                    filterOptions: {
                        enabled: true,
                        placeholder: "Client Name",
                    },
                },
                {
                    field: "totalContactCount",
                    label: "Contacts",
                    type: "number",
                    thClass: "health-count-header",
                    tdClass: "health-count-cell",
                    sortable: true,
                },
                {
                    field: "teamCount",
                    label: "Teams",
                    type: "number",
                    thClass: "health-count-header",
                    tdClass: "health-count-cell",
                    sortable: true,
                },
                {
                    field: "activeEmployeeCount",
                    label: "Active Members",
                    type: "number",
                    thClass: "health-count-header health-members-pad",
                    tdClass: "health-count-cell health-members-pad",
                    sortable: true,
                },
                {
                    field: "ageInMonths",
                    label: "Age",
                    type: "number",
                    thClass: "health-count-header",
                    tdClass: "health-count-cell",
                    sortable: true,
                },
            ];
            this.serverParams = {
                columnFilters: {},
                sort: {
                    field: "name",
                    type: "asc",
                },
                page: 1,
                pageSize: 10,
            };
        }
        // METHODS
        updateParams(newProps) {
            this.serverParams = Object.assign({}, this.serverParams, newProps);
        }
        onPageChange(params) {
            this.updateParams({ page: params.currentPage });
            this.loadItems();
        }
        onPerPageChange(params) {
            this.updateParams({ pageSize: params.currentPerPage, page: 1 });
            this.loadItems();
        }
        onSortChange(params) {
            this.updateParams({ sort: params[0], page: 1 });
            this.loadItems();
        }
        onColumnFilter(params) {
            this.updateParams({ columnFilters: params.columnFilters, page: 1 });
            this.loadItems();
        }
        loadItems() {
            this.isGridLoading = true;
            var searchData = {
                page: this.serverParams.page,
                pageSize: this.serverParams.pageSize,
                sort: this.serverParams.sort,
                columnFilters: this.columnFilters,
            };
            ClientModule.GetClientHealth(searchData)
                .then((response) => {
                this.rows = response?.clients ?? [];
                this.totalCount = response?.totalCount ?? 0;
            })
                .finally(() => {
                this.isGridLoading = false;
            });
        }
        // Switches between the active and the inactive clients; back to the first page
        onShowInactiveChange() {
            this.updateParams({ page: 1 });
            this.loadItems();
        }
        // The Show Inactive toggle is a view switch, not a column filter, so Clear Filter leaves it as is
        clearFilter() {
            this.serverParams.columnFilters = {};
            var ecgrid = this.$refs.ecgrid;
            ecgrid.$refs.grid.$refs["table-header-primary"].$refs["filter-row"].columnFilters = {};
            this.updateParams({ page: 1 });
            this.loadItems();
        }
        // Row click to Client Details is not part of the Health page for now
        // public onRowClick(params) {
        // 	this.$router.push({ name: "ClientDetails", params: { clientId: params.row.id } });
        // }
        isCountField(field) {
            return field === "totalContactCount" || field === "teamCount" || field === "activeEmployeeCount";
        }
        // A zero is a real value, shown quieter than a count that is in use
        isZeroCount(row, field) {
            const counts = {
                totalContactCount: row.totalContactCount,
                teamCount: row.teamCount,
                activeEmployeeCount: row.activeEmployeeCount,
            };
            return !counts[field];
        }
        // An empty or null value shows as "-"; a zero is a real value and is shown as is
        displayValue(value) {
            return value === null || value === undefined || value === "" ? "-" : value;
        }
        // Whole months since the Start Date, shown as years and months (e.g. 2y 3m); "-" when there is no Start Date
        formatAge(ageInMonths) {
            if (ageInMonths === null || ageInMonths === undefined) {
                return "-";
            }
            return `${Math.floor(ageInMonths / 12)}y ${ageInMonths % 12}m`;
        }
        // The dates the Age is measured between: to the End Date of a deactivated client, otherwise to today
        ageTooltip(row) {
            if (!row.startDate) {
                return "";
            }
            const to = row.endDate ? this.formatClientDate(row.endDate) : "today";
            return `From ${this.formatClientDate(row.startDate)} to ${to}`;
        }
        // Active clients wear their latest risk as a chip around the name (dashed when none is recorded).
        // The inactive view keeps a plain name.
        showNameChip() {
            return !this.showInactive;
        }
        // health-risk-red / -orange / -green; a level without a colour of its own keeps the plain grey chip
        nameRiskClass(row) {
            return row.latestRiskColor ? `health-risk-${clientRiskColorName(row.latestRiskColor)}` : "health-risk-none";
        }
        // Level name, then the description when one was recorded
        nameRiskTitle(row) {
            if (!row.latestRiskColor) {
                return "No risk recorded";
            }
            const label = this.riskColorShortLabel(row.latestRiskColor);
            const description = this.insightText(row.latestRiskDescription);
            const hasDescription = description !== "No description recorded.";
            if (label && hasDescription) {
                return `Risk: ${label} — ${description}`;
            }
            return label ? `Risk: ${label}` : description;
        }
        get columnFilters() {
            var filters = Object.entries(this.serverParams.columnFilters).map(([key, value]) => ({ name: key, value: value }));
            filters.push({
                name: "isActive",
                value: this.showInactive ? "Inactive" : "Active",
            });
            return filters;
        }
        // The Show Inactive toggle's isActive filter is always sent, so it doesn't count as an applied filter
        get isAnyFilterApplied() {
            return this.columnFilters.some((x) => x.name !== "isActive" && x.value);
        }
        created() {
            // Risk level names for the client-name tooltip
            ClientModule.GetRiskLevels();
        }
    };
    ClientHealth = __decorate([
        Component({
            name: "ClientHealth",
            components: {
                Layout,
                PageHeader,
                EcGrid,
                Switches,
            },
        })
    ], ClientHealth);
    return ClientHealth;
})();
export default ClientHealth;
